/**
 * Ghép file xuất Salework + AMIS thành danh sách SP import (TCR-2348, phương án B).
 *
 * Phân vai nguồn dữ liệu (theo spec):
 *  - AMIS     = nguồn của Mã (sku), Tên, Đơn vị tính, Nhóm VTHH (category).
 *  - Salework = nguồn của Giá bán lẻ, Barcode, Link ảnh.
 *  - Chỉ những mã có mặt ở CẢ HAI file mới được import; mã chỉ có ở Salework
 *    (thường là combo) trả về `unmatched` để UI báo cho người dùng.
 *
 * Toàn bộ là hàm thuần (không đọc file, không gọi API): đầu vào là mảng 2 chiều
 * từ `XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })`, nên test được không cần trình duyệt.
 *
 * TCR-2420 bổ sung chế độ "solo": chỉ cần file Salework (`buildSaleworkOnlyRows`), không ghép AMIS.
 * TCR-2421 bổ sung chế độ "chỉ AMIS" (`buildAmisOnlyRows`): chỉ cần file AMIS, không có giá/barcode/ảnh.
 * TCR-2421 (phần 2) `parseAmisRows` nhận thêm mẫu AMIS 4 cột Mã hàng/Tên hàng/ĐVT/Thương hiệu(Nhóm VTHH).
 */
export interface AmisBomComponent { sku: string; name: string; unit: string; qty: number }
export interface AmisItem {
  code: string; name: string; unit: string; group: string; status: string; stock: number;
  bom_components: AmisBomComponent[]; bom_errors: string[];
}
export interface SaleworkItem { code: string; name: string; price: number; barcode: string; image: string }
export interface ImportBomComponent { sku: string; name: string; unit: string; qty: number }
export interface ImportRow { sku: string; name: string; unit: string; category: string; price: number; barcode: string; image_url: string; is_combo?: boolean; is_bom_material?: boolean; bom_components?: ImportBomComponent[]; bom_errors?: string[] }
export interface MergeResult {
  /** Các dòng đã ghép, sẵn sàng gửi BE. */
  rows: ImportRow[];
  /** Mã có ở Salework nhưng không có ở AMIS. */
  unmatched: SaleworkItem[];
  /** Mã AMIS bị trùng VÀ khớp Salework (có trong `rows`): `kept` là tên dòng AMIS được giữ lại. Mã trùng ngoài phạm vi import không được báo. */
  duplicatesDropped: { code: string; kept: string }[];
}
/** Kết quả dựng dòng import ở chế độ chỉ Salework (TCR-2420). */
export interface SaleworkOnlyResult {
  /** Các dòng sẵn sàng gửi BE (ĐVT/Nhóm để rỗng — modal truyền Nhóm VTHH + Danh mục mặc định). */
  rows: ImportRow[];
  /** Mã không đủ dữ kiện để nhập, kèm lý do — hiện trên bước xem trước và tải về được. */
  skipped: { code: string; reason: string }[];
}
/** Kết quả dựng dòng import ở chế độ chỉ AMIS (TCR-2421). */
export interface AmisOnlyResult {
  /** Các dòng sẵn sàng gửi BE: Mã/Tên/ĐVT/Nhóm lấy từ AMIS, giá bán = 0, barcode/ảnh rỗng. */
  rows: ImportRow[];
  /** Mã không nhập được (thiếu mã/tên hoặc đã ngừng sử dụng), kèm lý do — tải về được. */
  skipped: { code: string; reason: string }[];
  /** Mã AMIS bị trùng VÀ nằm trong `rows`: `kept` là tên dòng AMIS được giữ lại. */
  duplicatesDropped: { code: string; kept: string }[];
  bomMaterialSkuCount: number;
}

function collectBom(items: AmisItem[]) {
  const components = new Map<string, AmisBomComponent>();
  const errors: string[] = [];
  for (const item of items) {
    errors.push(...(item.bom_errors ?? []));
    for (const component of item.bom_components ?? []) {
      const existing = components.get(component.sku);
      if (!existing) components.set(component.sku, { ...component });
      else if (existing.name !== component.name || existing.unit !== component.unit) errors.push(`Mã thành phần ${component.sku} có tên hoặc đơn vị không nhất quán`);
      else existing.qty += component.qty;
    }
  }
  return { components: [...components.values()], errors: [...new Set(errors)] };
}

function rejectNestedComboComponents(rows: ImportRow[]): ImportRow[] {
  const comboSkus = new Set(rows.filter((row) => row.is_combo).map((row) => row.sku));
  return rows.map((row) => {
    if (!row.is_combo) return row;
    const nested = (row.bom_components ?? [])
      .filter((component) => comboSkus.has(component.sku))
      .map((component) => `SKU thành phần ${component.sku} là một combo; không hỗ trợ BOM lồng nhau`);
    return nested.length
      ? { ...row, bom_errors: [...new Set([...(row.bom_errors ?? []), ...nested])] }
      : row;
  });
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
/** Ép số an toàn: chuỗi rác/âm/NaN → 0. */
const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
/**
 * Điểm ưu tiên khi 2 dòng AMIS trùng mã: "Đang sử dụng" thắng tuyệt đối, sau đó tồn kho lớn nhất.
 * Dùng chung cho `mergeSaleworkAmis` và `buildAmisOnlyRows` để 2 chế độ gộp trùng giống nhau.
 */
const amisRank = (a: AmisItem) => (a.status === 'Đang sử dụng' ? 1e15 : 0) + a.stock;

/**
 * Tìm dòng header trong 10 dòng đầu (AMIS có tiêu đề + dòng trống phía trên, header thật ở index 1/2).
 * Trả về index dòng header và map tên cột → chỉ số cột (cột trùng tên: lấy cột xuất hiện đầu tiên).
 * Không khớp `must` → trả `null` để nơi gọi thử tiếp mẫu header khác.
 */
function tryFindHeader(rows: unknown[][], must: string[]): { idx: number; col: Record<string, number> } | null {
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const cells = (rows[i] ?? []).map(str);
    if (must.every((m) => cells.includes(m))) {
      // reverse() để khi trùng tên cột thì giá trị đứng đầu (cột đầu tiên) ghi đè sau cùng.
      return { idx: i, col: Object.fromEntries(cells.map((c, j) => [c, j]).reverse()) as Record<string, number> };
    }
  }
  return null;
}

/** Như `tryFindHeader` nhưng ném `err` khi không có mẫu header nào khớp (dùng cho file Salework). */
function findHeader(rows: unknown[][], must: string[], err: string): { idx: number; col: Record<string, number> } {
  const found = tryFindHeader(rows, must);
  if (!found) throw new Error(err);
  return found;
}

/**
 * Chuẩn hoá cột "Thương hiệu" của mẫu AMIS 4 cột (thực chất là Nhóm VTHH): mỗi ô có thể chứa
 * NHIỀU nhóm ngăn bởi ';' và thường thừa ';'/khoảng trắng ở cuối → tách, trim, bỏ phần rỗng, nối lại bằng '; '.
 */
function normalizeAmisGroups(v: unknown): string {
  return str(v).split(';').map((s) => s.trim()).filter(Boolean).join('; ');
}

/**
 * Đọc file AMIS. Hỗ trợ 2 mẫu xuất đang gặp (thử mẫu cũ trước để không đổi hành vi cũ):
 *  1. "Danh sách hàng hóa, dịch vụ": header có 'Mã' + 'Tên' (kèm Đơn vị tính chính / Nhóm VTHH /
 *     Trạng thái / Số lượng tồn nếu có).
 *  2. Bảng 4 cột 'Mã hàng' / 'Tên hàng' / 'ĐVT' / 'Thương hiệu' (Thương hiệu = Nhóm VTHH, không có
 *     trạng thái/tồn kho → status = '', stock = 0).
 * Không khớp cả 2 mẫu → ném FILE_AMIS_INVALID.
 */
export function parseAmisRows(rows: unknown[][]): AmisItem[] {
  return parseAmisWorkbook([{ name: '', rows }]);
}

/** Parse mọi sheet AMIS có header hàng hóa đã nhận diện; sheet phụ khác header được bỏ qua. SKU trùng được giữ để bước dựng import áp dụng quy tắc chọn bản ghi thắng. */
export function parseAmisWorkbook(sheets: { name: string; rows: unknown[][] }[]): AmisItem[] {
  const result: AmisItem[] = [];
  const byCode = new Map<string, AmisItem>();
  for (const sheet of sheets) {
    const rows = sheet.rows;
    const legacy = tryFindHeader(rows, ['Mã', 'Tên']);
    let found = legacy;
    let short = null as ReturnType<typeof tryFindHeader>;
    if (!found) short = tryFindHeader(rows, ['Mã hàng', 'Tên hàng', 'ĐVT', 'Thương hiệu']);
    if (!found && !short) continue;
    const { idx, col } = found ?? short!;
    const legacyMode = Boolean(found);
    const codeCol = legacyMode ? col['Mã'] : col['Mã hàng'];
    const nameCol = legacyMode ? col['Tên'] : col['Tên hàng'];
    const productEnd = idx + 1;
    // BOM labels are an AMIS subheader on the row immediately after product headers.
    const sub = rows[productEnd] ?? [];
    const bomLabels = ['Mã nguyên vật liệu', 'Tên nguyên vật liệu', 'Đơn vị tính', 'Số lượng'];
    const subLabels = sub.map(str);
    const bomStart = subLabels.findIndex((_, index) => bomLabels.every((expected, offset) => subLabels[index + offset] === expected));
    const hasBom = bomStart >= 0;
    const bomCol = Object.fromEntries(bomLabels.map((label, offset) => [label, hasBom ? bomStart + offset : -1]));
    const dataStart = hasBom ? productEnd + 1 : productEnd;
    for (const r of rows.slice(dataStart)) {
      const code = str(r[codeCol]);
      if (!code) continue;
      const item: AmisItem = {
        code,
        name: str(r[nameCol]),
        unit: str(r[legacyMode ? col['Đơn vị tính chính'] : col['ĐVT']]),
        group: legacyMode ? str(r[col['Nhóm VTHH']]) : normalizeAmisGroups(r[col['Thương hiệu']]),
        status: legacyMode && col['Trạng thái'] != null ? str(r[col['Trạng thái']]) : '',
        stock: legacyMode ? Number(r[col['Số lượng tồn']]) || 0 : 0,
        bom_components: [], bom_errors: [],
      };
      const target = hasBom ? byCode.get(code) : undefined;
      const parent = target ?? item;
      if (hasBom) {
        const sku = str(r[bomCol['Mã nguyên vật liệu']]);
        const componentName = str(r[bomCol['Tên nguyên vật liệu']]);
        const componentUnit = str(r[bomCol['Đơn vị tính']]);
        const rawQty = r[bomCol['Số lượng']];
        const touched = Boolean(sku || componentName || componentUnit || (rawQty != null && str(rawQty)));
        if (touched) {
          const qty = Number(rawQty);
          if (!sku) parent.bom_errors.push('Thiếu mã thành phần');
          if (!componentName) parent.bom_errors.push(`Thiếu tên thành phần${sku ? ` ${sku}` : ''}`);
          if (!componentUnit) parent.bom_errors.push(`Thiếu đơn vị thành phần${sku ? ` ${sku}` : ''}`);
          if (str(rawQty) === '' || !Number.isFinite(qty) || qty <= 0 || !Number.isInteger(qty)) parent.bom_errors.push(`Số lượng thành phần không hợp lệ${sku ? ` ${sku}` : ''}`);
          if (sku && componentName && componentUnit && Number.isInteger(qty) && qty > 0) {
            const existing = parent.bom_components.find((c) => c.sku === sku);
            if (!existing) parent.bom_components.push({ sku, name: componentName, unit: componentUnit, qty });
            else if (existing.name !== componentName || existing.unit !== componentUnit) parent.bom_errors.push(`Mã thành phần ${sku} có tên hoặc đơn vị không nhất quán`);
            else existing.qty += qty;
          }
        }
      }
      if (target) {
        target.bom_errors.push(...item.bom_errors);
        // Product metadata belongs to the first row for the parent; each repeated row contributes BOM only.
      } else {
        if (hasBom) byCode.set(code, item);
        result.push(item);
      }
    }
  }
  if (!result.length) throw new Error('FILE_AMIS_INVALID');
  return result;
}

/** Đọc file Salework (sheet KHO_HANG). Ném FILE_SALEWORK_INVALID nếu thiếu header. */
export function parseSaleworkRows(rows: unknown[][]): SaleworkItem[] {
  const { idx, col } = findHeader(rows, ['MÃ SẢN PHẨM', 'TÊN SẢN PHẨM'], 'FILE_SALEWORK_INVALID');
  const out: SaleworkItem[] = [];
  for (const r of rows.slice(idx + 1)) {
    const parent = str(r[col['MÃ SẢN PHẨM']]);
    const child = str(r[col['MÃ SP TRONG COMBO']]);
    const code = child || parent; // dòng SP con mang mã con; dòng combo mang mã combo
    if (!code) continue;
    out.push({
      code, name: str(r[col['TÊN SẢN PHẨM']]), price: num(r[col['GIÁ BÁN LẺ']]),
      barcode: str(r[col['BARCODE']]), image: str(r[col['LINK ẢNH']]),
    });
  }
  return out;
}

/** Ghép Salework + AMIS theo mã. Xem mô tả đầu file về phân vai nguồn. */
export function mergeSaleworkAmis(sw: SaleworkItem[], amis: AmisItem[]): MergeResult {
  // Gộp AMIS trùng mã: "Đang sử dụng" thắng, sau đó tồn kho lớn nhất.
  const best = new Map<string, AmisItem>();
  const duplicatesDropped: MergeResult['duplicatesDropped'] = [];
  for (const a of amis) {
    const cur = best.get(a.code);
    if (!cur) { best.set(a.code, a); continue; }
    best.set(a.code, amisRank(a) > amisRank(cur) ? a : cur);
  }
  // Đếm số dòng AMIS theo mã để biết mã nào bị trùng (chỉ báo cáo mã trùng NẰM TRONG phạm vi import, xem bên dưới).
  const counts = new Map<string, number>();
  amis.forEach((a) => counts.set(a.code, (counts.get(a.code) ?? 0) + 1));

  // Mỗi mã Salework xuất hiện nhiều lần (SP con thuộc nhiều combo) → chỉ lấy 1 lần, giữ giá trị không rỗng đầu tiên.
  const swByCode = new Map<string, SaleworkItem>();
  for (const s of sw) {
    const cur = swByCode.get(s.code);
    swByCode.set(s.code, cur ? { ...cur, price: cur.price || s.price, barcode: cur.barcode || s.barcode, image: cur.image || s.image } : s);
  }

  const rows: ImportRow[] = []; const unmatched: SaleworkItem[] = [];
  swByCode.forEach((s, code) => {
    const a = best.get(code);
    if (!a) { unmatched.push(s); return; }
    // Chỉ mã đã khớp Salework (thực sự vào `rows`) mới được báo là "trùng đã gộp".
    if ((counts.get(code) ?? 0) > 1) duplicatesDropped.push({ code, kept: a.name });
    const bom = collectBom(amis.filter((item) => item.code === code));
    rows.push({ sku: code, name: a.name, unit: a.unit, category: a.group, price: s.price, barcode: s.barcode, image_url: s.image,
      ...(bom.components.length || bom.errors.length ? { is_combo: true, bom_components: bom.components, bom_errors: bom.errors } : {}) });
  });
  const validatedRows = rejectNestedComboComponents(rows);
  const covered = new Set(validatedRows.filter((row) => !row.is_combo).map((row) => row.sku));
  const inferred = new Map<string, ImportRow>();
  for (const row of validatedRows.filter((item) => item.is_combo && !item.bom_errors?.length)) {
    for (const component of row.bom_components ?? []) if (!covered.has(component.sku) && !inferred.has(component.sku)) {
      inferred.set(component.sku, { sku: component.sku, name: component.name, unit: component.unit, category: '', price: 0, barcode: '', image_url: '', is_bom_material: true });
    }
  }
  validatedRows.push(...inferred.values());
  validatedRows.sort((a, b) => Number(Boolean(a.is_combo)) - Number(Boolean(b.is_combo)));
  return { rows: validatedRows, unmatched, duplicatesDropped };
}

/**
 * Chế độ solo (TCR-2420): dựng dòng import chỉ từ file Salework, không cần file AMIS.
 *
 * - Gộp mã trùng giống `mergeSaleworkAmis`: mỗi mã 1 dòng, giữ giá trị không rỗng
 *   ĐẦU TIÊN cho name/price/barcode/image (SP con nằm trong nhiều combo → lặp mã).
 * - ĐVT và Nhóm VTHH để rỗng vì Salework không có 2 cột này; modal truyền
 *   `inventory_item_group_id` + `default_category_id` để BE gán khi tạo SP.
 * - Dòng thiếu tên bị loại (BE bắt buộc tên) và ghi vào `skipped` để người dùng rà lại.
 */
export function buildSaleworkOnlyRows(sw: SaleworkItem[]): SaleworkOnlyResult {
  // Mỗi mã Salework chỉ giữ 1 bản ghi đã "làm đầy" các trường còn thiếu từ bản ghi trước.
  const byCode = new Map<string, SaleworkItem>();
  for (const s of sw) {
    const cur = byCode.get(s.code);
    byCode.set(s.code, cur ? {
      ...cur,
      name: cur.name || s.name,
      price: cur.price || s.price,
      barcode: cur.barcode || s.barcode,
      image: cur.image || s.image,
    } : s);
  }

  const rows: ImportRow[] = [];
  const skipped: SaleworkOnlyResult['skipped'] = [];
  byCode.forEach((s, code) => {
    if (!s.name) { skipped.push({ code, reason: 'Thiếu tên sản phẩm' }); return; }
    rows.push({
      sku: code, name: s.name, unit: '', category: '',
      price: s.price, barcode: s.barcode, image_url: s.image,
    });
  });
  return { rows, skipped };
}

/**
 * Chế độ chỉ AMIS (TCR-2421): dựng dòng import chỉ từ file AMIS, không cần file Salework.
 *
 * - Mã/Tên/ĐVT/Nhóm VTHH lấy từ AMIS; giá bán = 0, barcode/ảnh rỗng vì Salework là nguồn
 *   của 3 trường này → người dùng bổ sung sau khi nhập.
 * - Gộp mã trùng theo ĐÚNG luật của `mergeSaleworkAmis` ("Đang sử dụng" thắng, rồi tồn kho
 *   lớn nhất) TRƯỚC khi lọc, và chỉ báo "trùng đã gộp" cho mã thực sự vào `rows`.
 * - Dòng thiếu mã/tên hoặc đã "Ngừng..." bị loại và ghi vào `skipped` kèm lý do để người dùng rà lại.
 */
export function buildAmisOnlyRows(amis: AmisItem[]): AmisOnlyResult {
  const best = new Map<string, AmisItem>();
  const counts = new Map<string, number>();
  const skipped: AmisOnlyResult['skipped'] = [];
  for (const a of amis) {
    // Dòng không có mã không gộp được → báo ngay, không đưa vào map.
    if (!a.code) { skipped.push({ code: '', reason: 'Thiếu mã sản phẩm' }); continue; }
    counts.set(a.code, (counts.get(a.code) ?? 0) + 1);
    const cur = best.get(a.code);
    if (!cur) { best.set(a.code, a); continue; }
    best.set(a.code, amisRank(a) > amisRank(cur) ? a : cur);
  }

  const rows: ImportRow[] = [];
  const duplicatesDropped: AmisOnlyResult['duplicatesDropped'] = [];
  best.forEach((a, code) => {
    if (!a.name) { skipped.push({ code, reason: 'Thiếu tên sản phẩm' }); return; }
    // Lọc sau khi gộp: bản được giữ lại là "Ngừng..." thì cả mã bị bỏ (bản đang dùng đã thua điểm).
    if (a.status.includes('Ngừng')) { skipped.push({ code, reason: 'Ngừng sử dụng' }); return; }
    if ((counts.get(code) ?? 0) > 1) duplicatesDropped.push({ code, kept: a.name });
    const bom = collectBom(amis.filter((item) => item.code === code));
    rows.push({
      sku: code, name: a.name, unit: a.unit, category: a.group,
      price: 0, barcode: '', image_url: '',
      ...(bom.components.length || bom.errors.length ? { is_combo: true, bom_components: bom.components, bom_errors: bom.errors } : {}),
    });
  });
  const validatedRows = rejectNestedComboComponents(rows);
  const covered = new Set(validatedRows.filter((row) => !row.is_combo).map((row) => row.sku));
  const inferred = new Map<string, ImportRow>();
  for (const row of validatedRows.filter((item) => item.is_combo)) {
    if (row.bom_errors?.length) continue;
    for (const component of row.bom_components ?? []) {
      if (covered.has(component.sku)) continue;
      const prior = inferred.get(component.sku);
      if (!prior) inferred.set(component.sku, { sku: component.sku, name: component.name, unit: component.unit, category: '', price: 0, barcode: '', image_url: '', is_combo: false, is_bom_material: true });
    }
  }
  const ordered = [...validatedRows.filter((row) => !row.is_combo), ...inferred.values(), ...validatedRows.filter((row) => row.is_combo)];
  const bomMaterialSkuCount = inferred.size;
  return { rows: ordered, skipped, duplicatesDropped, bomMaterialSkuCount };
}
